import { useQuery } from "@tanstack/react-query";
import { useUser } from "@auth";
import api from "./api";
import { queryClient } from "./queryClient";

const UI_PREFERENCES_KEY = ["uiPreferences"];

// Type prefixes that are scoped to the individual user rather than the whole merchant.
// Add new prefixes here when introducing user-scoped preference types.
const USER_SCOPED_PREFIXES = ["column:", "workingSet:"];

const getScopeType = (type) =>
  USER_SCOPED_PREFIXES.some((prefix) => type.startsWith(prefix))
    ? "User"
    : "Merchant";

async function getUiPreferences(userId) {
  try {
    const response = await api.get("api/uiPreferences");
    return response.data.filter(
      (p) => p.scopeType === "Merchant" || p.createdByUserId === userId,
    );
  } catch (error) {
    console.error(error);
    return [];
  }
}

async function getUiPreferenceById(id) {
  try {
    const response = await api.get(`api/uiPreferences/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    return null;
  }
}

async function createUiPreference({ name, type, value }) {
  try {
    const response = await api.post("api/uiPreferences", {
      name,
      type,
      value,
      scopeType: getScopeType(type),
    });
    queryClient.invalidateQueries({ queryKey: UI_PREFERENCES_KEY });
    return response.data;
  } catch (error) {
    console.error(error);
    return null;
  }
}

async function updateUiPreference(id, { name, value }) {
  try {
    await api.put(`api/uiPreferences/${id}`, { name, value });
    queryClient.invalidateQueries({ queryKey: UI_PREFERENCES_KEY });
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

async function deleteUiPreference(id) {
  try {
    await api.delete(`api/uiPreferences/${id}`);
    queryClient.invalidateQueries({ queryKey: UI_PREFERENCES_KEY });
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

function useUiPreferences() {
  const { user } = useUser();
  return useQuery({
    queryKey: ["uiPreferences", user?.guid],
    queryFn: () => getUiPreferences(user?.guid),
    enabled: !!user?.guid,
    staleTime: 60_000,
  });
}

export {
  getUiPreferences,
  getUiPreferenceById,
  createUiPreference,
  updateUiPreference,
  deleteUiPreference,
  useUiPreferences,
};
